import colors from 'tailwindcss/colors';

export const hexPattern = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const normalizeHex = (hex: string) => {
	let h = hex.replace('#', '').toLowerCase();
	if (h.length === 3) {
		h = h
			.split('')
			.map((c) => c + c)
			.join('');
	}
	return `#${h}`;
};

const parseRgbStringToHex = (input: string): string | null => {
	const m = input.trim().match(/^rgba?\(\s*([^)]+)\)/i);
	if (!m) return null;
	const parts = m[1]
		.trim()
		.split(/[\s,/]+/)
		.filter(Boolean);
	if (parts.length < 3) return null;
	const r = parseFloat(parts[0]);
	const g = parseFloat(parts[1]);
	const b = parseFloat(parts[2]);
	if ([r, g, b].some((n) => Number.isNaN(n))) return null;
	const toHex = (n: number) =>
		Math.round(Math.min(255, Math.max(0, n)))
			.toString(16)
			.padStart(2, '0');
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

function resolveTailwindBgClassToHex(v: string): string | null {
	const lower = v.trim().toLowerCase();
	if (lower === 'bg-white') return '#ffffff';
	if (lower === 'bg-black') return '#000000';
	if (lower === 'bg-transparent') return null;

	const arbitrary = v.match(/^bg-\[(#[0-9a-fA-F]{3,8})\]$/i);
	if (arbitrary) {
		let hex = arbitrary[1];
		if (hex.length === 4 && hex.startsWith('#')) {
			const h = hex.slice(1);
			hex = `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
		}
		return hexPattern.test(hex) ? normalizeHex(hex) : null;
	}

	const m = v.match(/^bg-([a-z]+)-(\d{2,3})$/i);
	if (!m) return null;
	const colorName = m[1].toLowerCase();
	const shade = m[2];
	const palette = colors[colorName as keyof typeof colors];
	if (!palette || typeof palette !== 'object') return null;
	const shadeValue = (palette as Record<string, string>)[shade];
	if (typeof shadeValue !== 'string' || !shadeValue.startsWith('#'))
		return null;
	return shadeValue;
}

/** Hex (# or rgb/rgba) ou classe Tailwind `bg-*` → cor sólida para fundo e contraste. */
export function resolveColorInputToHex(input: string): string | null {
	const v = input.trim();
	if (!v) return null;
	if (v.startsWith('#')) {
		return hexPattern.test(v) ? normalizeHex(v) : null;
	}
	if (v.toLowerCase().startsWith('rgb')) {
		return parseRgbStringToHex(v);
	}
	return resolveTailwindBgClassToHex(v);
}

export function getContrastTextClassForHex(hex: string): string {
	if (!hexPattern.test(hex)) return 'text-white';
	const n = normalizeHex(hex).replace('#', '');
	const r = parseInt(n.slice(0, 2), 16) / 255;
	const g = parseInt(n.slice(2, 4), 16) / 255;
	const b = parseInt(n.slice(4, 6), 16) / 255;

	const [R, G, B] = [r, g, b].map((c) =>
		c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
	);
	const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

	return luminance > 0.6 ? 'text-zinc-900' : 'text-white';
}

/** Aceita hex, rgb/rgba ou `bg-*` (Tailwind). */
export function getContrastTextClassForColorInput(
	input: string,
	fallbackHex = '#e5e7eb',
): string {
	const hex = resolveColorInputToHex(input) ?? fallbackHex;
	return getContrastTextClassForHex(hex);
}
